import { useState } from 'react';
import { useTranslation } from '@frontend/i18n';
import { useInboxMutePolicy } from '@frontend/inbox-mute-policy';
import { InboxPrefixes } from '@frontend/tracking-prefixes';
import { theme } from '@frontend/theme';
import { PrimaryButton, usePopoverDialog, PopoverDialog } from '@frontend/design-system';
import {
  useInboxFiltersShallowStore,
  inboxFiltersAreEqual,
  DEFAULT_INBOX_FILTER_VALUE,
} from '../../stores/inbox-store';
import { HiddenFilterKeys, InboxArrayFilters, InboxFilter, InboxFilters } from '../../types';
import { DateFilters } from './date-filters/date-filters';
import { InboxFilterPopoverMenuItem } from './inbox-filter-popover-menu-item';
import { InboxFiltersPopoverHeadingItem } from './inbox-filters-popover-heading-item';
import { TimeFilters } from './time-filters/time-filters';
import { DateTimeSelectionHandlerKeys } from './types';

type InboxFiltersPopoverProps = Pick<
  ReturnType<typeof usePopoverDialog<HTMLButtonElement>>,
  'getDialogProps' | 'close'
> & {
  groupIds?: string[];
  hideFilters?: HiddenFilterKeys;
  /** If `filters` and `setFilters` is provided, instead of using the inbox filters store, the provided state will be used */
  filters?: InboxFilters;
  setFilters?: (newFilters: InboxFilters) => void;
};

export const InboxFiltersPopover = ({
  getDialogProps,
  close,
  groupIds,
  hideFilters,
  filters,
  setFilters,
}: InboxFiltersPopoverProps) => {
  const { t } = useTranslation('inbox');
  const { isHidden } = useInboxMutePolicy();
  const { inboxFilters: storeFilters, setInboxFilters: setStoreFilters } = useInboxFiltersShallowStore(
    'inboxFilters',
    'setInboxFilters'
  );
  const inboxFilters = filters ?? storeFilters;
  const setInboxFilters = setFilters ?? setStoreFilters;
  const shownInboxFilters = Object.fromEntries(
    Object.entries(inboxFilters).filter(([key]) => !hideFilters?.includes(key as InboxFilter))
  ) as Partial<InboxFilters>;
  const [newSelection, setNewSelection] = useState<typeof shownInboxFilters>();
  const currentSelection = newSelection ?? shownInboxFilters;
  const hasChangedSelection = !!newSelection && !inboxFiltersAreEqual(currentSelection, shownInboxFilters);

  const showDateOrTimeFilters = ['date', 'time'].filter((filter) => !hideFilters?.includes(filter as InboxFilter));
  const shownFiltersOrder: InboxArrayFilters[] = ['tags', 'locationPhones', 'tasks', 'aiReceptionist'].filter(
    (filter) =>
      !hideFilters?.includes(filter as InboxArrayFilters) &&
      !(filter === 'tags' && isHidden('filter-tags')) &&
      !(filter === 'tasks' && isHidden('filter-tasks')) &&
      !(filter === 'aiReceptionist' && isHidden('filter-ai-receptionist'))
  ) as InboxArrayFilters[];

  const handleToggle = (key: InboxArrayFilters, values: string[]) => {
    setNewSelection((prev) => {
      const currentValues = prev ?? shownInboxFilters;
      const prevValues = (currentValues[key] || []) as string[];

      const result = values.reduce<{ toRemove: string[]; toAdd: string[] }>(
        (acc, value) => {
          if (prevValues.includes(value)) {
            acc.toRemove.push(value);
          } else {
            acc.toAdd.push(value);
          }
          return acc;
        },
        { toRemove: [], toAdd: [] }
      );

      return {
        ...currentValues,
        [key]: prevValues.filter((v) => !result.toRemove.includes(v)).concat(result.toAdd),
      };
    });
  };

  const handleApply = () => {
    setInboxFilters({ ...inboxFilters, ...newSelection });
    setNewSelection(undefined);
    close();
  };

  const handleClear = (filter?: string) => {
    setNewSelection((oldSelection) => {
      const oldValues = oldSelection || currentSelection;

      let valueToUpdate;
      if (filter === 'date') {
        valueToUpdate = { startDate: '', endDate: '' };
      } else if (filter === 'time') {
        valueToUpdate = { startTime: '', endTime: '' };
      } else {
        const { startDate, endDate, startTime, endTime, ...rest } = DEFAULT_INBOX_FILTER_VALUE;
        valueToUpdate = { ...rest };
      }

      return { ...oldValues, ...valueToUpdate };
    });
  };

  const handleDateTimeSelection = (key: DateTimeSelectionHandlerKeys, value: string) => {
    setNewSelection((prev) => {
      const currentValues = prev ?? shownInboxFilters;
      return {
        ...currentValues,
        [key]: value,
      };
    });
  };

  return (
    <PopoverDialog
      {...getDialogProps()}
      css={{
        width: showDateOrTimeFilters.length ? 300 : 240,
        padding: theme.spacing(1, 0),
        borderRadius: theme.borderRadius.medium,
      }}
    >
      {!!showDateOrTimeFilters.includes('date') && (
        <>
          <InboxFiltersPopoverHeadingItem
            label={t('Scheduled Date')}
            handleClear={() => handleClear('date')}
            hasSelection={!!(currentSelection?.startDate || currentSelection?.endDate)}
            clearButtonId='date-clear-button'
            trackingId={`${InboxPrefixes.List}-filter-popover-date-clear`}
          />
          <div css={{ paddingTop: theme.spacing(1) }}>
            <DateFilters
              onDateTimeSelection={handleDateTimeSelection}
              initialValue={{
                startDate: currentSelection?.startDate ?? '',
                endDate: currentSelection?.endDate ?? '',
              }}
            />
          </div>
        </>
      )}
      {!!showDateOrTimeFilters.includes('time') && (
        <>
          <InboxFiltersPopoverHeadingItem
            label={t('Send Time')}
            handleClear={() => handleClear('time')}
            hasSelection={!!(currentSelection?.startTime || currentSelection?.endTime)}
            clearButtonId='time-clear-button'
            trackingId={`${InboxPrefixes.List}-filter-popover-time-clear`}
          />
          <div css={{ padding: theme.spacing(1, 0, 2, 0), borderBottom: `1px solid ${theme.colors.neutral20}` }}>
            <TimeFilters
              onDateTimeSelection={handleDateTimeSelection}
              initialValue={{
                startTime: currentSelection?.startTime ?? '',
                endTime: currentSelection?.endTime ?? '',
              }}
            />
          </div>
        </>
      )}
      <InboxFiltersPopoverHeadingItem
        label={!!showDateOrTimeFilters.length ? t('Other Filters') : t('Filter Inbox')}
        handleClear={handleClear}
        hasSelection={!!shownFiltersOrder.find((filter) => !!currentSelection[filter]?.length)}
        clearButtonId='clear-inbox-filters-btn'
        largeLabel={!showDateOrTimeFilters.length}
        trackingId={`${InboxPrefixes.List}-filter-popover-clear`}
      />
      {shownFiltersOrder.map((filter, index) => (
        <InboxFilterPopoverMenuItem
          filter={filter}
          key={filter + index}
          selectedItems={currentSelection[filter] ?? []}
          toggleItems={(values) => handleToggle(filter, values)}
          divider={index > 0 ? 'top' : undefined}
          groupIds={groupIds}
          closeMenu={close}
        />
      ))}
      <div
        css={{
          padding: theme.spacing(1, 2),
        }}
      >
        <PrimaryButton
          disabled={!hasChangedSelection}
          onClick={handleApply}
          trackingId={`${InboxPrefixes.List}-filter-popover-apply`}
        >
          {t('Apply')}
        </PrimaryButton>
      </div>
    </PopoverDialog>
  );
};
